import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { StyledHeaderTooltip, TooltipContentWrapper, TooltipCloseButtonBox, TooltipCloseIconButton } from './styles';

const HeaderTooltip = ({ 
  title, 
  showCloseIcon, 
  onOpen, 
  onClose, 
  open: controlledOpen,
  children,
  ...props 
}) => {

  /**
   * State Management Logic:
   * This component supports both controlled and uncontrolled modes for the tooltip visibility.
   * 
   * - Controlled mode: The parent component passes the `open` prop. The parent is responsible for managing the state.
   * - Uncontrolled mode: The `open` prop is undefined. This component manages its own `internalOpen` state.
   */


  // Internal state used only when the component is uncontrolled
  const [internalOpen, setInternalOpen] = useState(false);

  // Check if the component is being controlled by the parent
  const isControlled = controlledOpen !== undefined;

  // Derive the current open state based on the mode
  const isOpen = isControlled ? controlledOpen : internalOpen;

  /**
   * Handler for opening the tooltip.
   * If uncontrolled, we update the internal state to show the tooltip.
   * We always call the `onOpen` prop if it exists, allowing parents to react to open events in both modes.
   */

  const handleOpen = (event) => {
    if (!isControlled) setInternalOpen(true);
    if (onOpen) onOpen(event);
  };

  /**
   * Handler for closing the tooltip.
   * If uncontrolled, we update the internal state to hide the tooltip.
   * We always call the `onClose` prop if it exists.
   */

  const handleClose = (event) => {
    if (!isControlled) setInternalOpen(false);
    if (onClose) onClose(event);
  };

  const handleCloseIconClick = (event) => {
    event.stopPropagation();
    handleClose(event);
  };

  const tooltipContent = showCloseIcon ? (
    <TooltipContentWrapper>
      <TooltipCloseButtonBox
      >
        <TooltipCloseIconButton
          aria-label="Close tooltip"
          onClick={handleCloseIconClick}
        >
          <CloseIcon />
        </TooltipCloseIconButton>
      </TooltipCloseButtonBox>
      {title}
    </TooltipContentWrapper>
  ) : (
    title
  );

  return (
    <StyledHeaderTooltip
      {...props}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      title={tooltipContent}
      disableFocusListener
    >
      <Box component="span">
        {children}
      </Box>
    </StyledHeaderTooltip>
  );
};

HeaderTooltip.propTypes = {
  variant: PropTypes.oneOf(['country', 'cart']),
  title: PropTypes.node.isRequired,
  children: PropTypes.node.isRequired,
  open: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func,
  arrow: PropTypes.bool,
  placement: PropTypes.string,
  showCloseIcon: PropTypes.bool,
};

export default HeaderTooltip;